// "Reach a level": what to check, how the target is set, and where the chosen audience stands
// before anything is sent. A score, a month and a number per store category IS today's
// Reach a score: the server sees that, stores it as one, and gives today's counts and line.

import { useEffect, useState } from 'react';
import {
  bannerAPI,
  type BannerAudienceBody,
  type DetailRef,
  type LevelSourceOption,
  type LevelTargetInput,
  type ReachLevelInput,
  type ReachLevelPreview,
  type ReachLevelSources,
} from './bannerApi';
import { COUNT_DEBOUNCE_MS, monthLabel, previousMonth, type TaskPick } from './bannerOptions';
import { detailKey, detailLabel, type BannerDetails } from './useBannerDetails';

export type TargetMode = 'fixed' | 'own' | 'per_value';

const STORE_CATEGORY = 'store|column|store_category';

/** A choice's identity: what the server sent is the only thing that says which it is. */
const sourceKey = (option: LevelSourceOption): string => JSON.stringify(option.source);
const isTargetNumber = (raw: string): boolean => Number.isFinite(Number(raw)) && Number(raw) >= 0;
const asRef = ({ scope, source, key }: DetailRef): DetailRef => ({ scope, source, key });

export function useReachLevel({
  on,
  projectId,
  hasAudience,
  audience,
  details,
  onPick,
}: {
  on: boolean;
  projectId: number | null;
  hasAudience: boolean;
  audience: BannerAudienceBody;
  details: BannerDetails;
  /** The default copy names the score and the month, so it follows both until edited. */
  onPick: (pick: Pick<TaskPick, 'scoreName' | 'month' | 'hasValue'>) => void;
}) {
  const [sources, setSources] = useState<(ReachLevelSources & { projectId: number }) | null>(null);
  const [sourcesError, setSourcesError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [pickedKey, setPickedKey] = useState('');
  const [month, setMonth] = useState(previousMonth);
  const [chosenMode, setMode] = useState<TargetMode>('fixed');
  const [fixedValue, setFixedValue] = useState('');
  const [ownKey, setOwnKey] = useState('');
  const [perKey, setPerKey] = useState('');
  // Detail → value → what is typed in its box. Absent: its suggested level, if it has one. Kept
  // per detail, so picking the wrong one by mistake costs nothing.
  const [typedByDetail, setTypedByDetail] = useState<Record<string, Record<string, string>>>({});
  // Detail → values typed in by hand: one with more values than the list holds.
  const [addedByDetail, setAddedByDetail] = useState<Record<string, string[]>>({});
  // The count, with the question it answers: a change of audience can never lean on an old one.
  const [counted, setCounted] = useState<{ body: string; tiles: string; data: ReachLevelPreview | null } | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Bumped by Try again, to count again after a failure.
  const [previewAttempt, setPreviewAttempt] = useState(0);

  const loaded = sources?.projectId === projectId ? sources : null;
  const picked = loaded?.sources.find((o) => sourceKey(o) === pickedKey) ?? null;
  const ownField = loaded?.ownTargets.find((o) => detailKey(o) === ownKey) ?? null;
  const perField = details.fieldOf(perKey);
  const isScoreSource = picked?.source.kind === 'score_parameter';
  const hasValue = !!picked?.hasValueOnly;
  // Nothing on this project holds a number of its own, or has values to set one by: that way
  // of setting a target would lead nowhere, so it is not offered.
  const offersOwn = !loaded || loaded.ownTargets.length > 0;
  const offersPer = !details.loaded || details.targetFieldOptions.length > 0;
  const mode: TargetMode =
    (chosenMode === 'own' && !offersOwn) || (chosenMode === 'per_value' && !offersPer) ? 'fixed' : chosenMode;
  const typed = typedByDetail[perKey] ?? {};
  const perValues = perField ? [...new Set([...perField.values, ...(addedByDetail[perKey] ?? [])])] : [];
  const nameOfMonth = picked?.needsMonth ? month : '';

  // The project's own list. Loaded when the task is first picked, and again for a different
  // project, whose choices cannot carry over.
  useEffect(() => {
    if (!on || projectId == null || sources?.projectId === projectId) return;
    if (sources) {
      setPickedKey('');
      setOwnKey('');
      setPerKey('');
      setTypedByDetail({});
      setAddedByDetail({});
      onPick({ scoreName: null, month: '', hasValue: false });
    }
    let cancelled = false;
    setSourcesError(null);
    bannerAPI
      .reachLevelSources(projectId)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data;
        setSources({ sources: data?.sources ?? [], ownTargets: data?.ownTargets ?? [], projectId });
      })
      .catch((err: any) => {
        if (!cancelled) setSourcesError(err?.response?.data?.message || 'Could not load what can be checked.');
      });
    return () => {
      cancelled = true;
    };
    // Only a new task, project or retry reloads; the copy reset reads the rest as they are.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on, projectId, sources?.projectId, attempt]);

  // Store category on a score: each box starts at its SEED level, as Reach a score's did.
  const boxValue = (value: string): string => {
    const own = typed[value];
    if (own !== undefined) return own;
    const seed = isScoreSource && perKey === STORE_CATEGORY ? perField?.seedLevels?.[value] : undefined;
    return seed !== undefined ? String(seed) : '';
  };
  // The boxes with something in them, in the grid's order. A blank box is no target.
  const perRows = perValues.flatMap((value) => {
    const raw = boxValue(value).trim();
    return raw ? [{ value, raw }] : [];
  });

  const pickSource = (key: string) => {
    setPickedKey(key);
    const option = loaded?.sources.find((o) => sourceKey(o) === key) ?? null;
    onPick({ scoreName: option?.label ?? null, month: option?.needsMonth ? month : '', hasValue: !!option?.hasValueOnly });
  };
  const pickMonth = (next: string) => {
    setMonth(next);
    onPick({ scoreName: picked?.label ?? null, month: next, hasValue: false });
  };
  const typeBox = (value: string, text: string) =>
    setTypedByDetail((prev) => ({ ...prev, [perKey]: { ...prev[perKey], [value]: text } }));
  // A value past the list's limit, typed by hand: the server matches it all the same.
  const addPerValue = (raw: string) => {
    const value = raw.trim();
    if (!value || perValues.some((v) => v.toLowerCase() === value.toLowerCase())) return;
    setAddedByDetail((prev) => ({ ...prev, [perKey]: [...(prev[perKey] ?? []), value] }));
  };

  // What still stops a send, in the order the form asks for it.
  const missing = !on
    ? null
    : projectId == null
    ? 'Pick a project first.'
    : sourcesError
    ? 'What can be checked could not be loaded. Try again below.'
    : !loaded
    ? 'Loading what can be checked…'
    : !picked
    ? 'Pick what to check.'
    : picked.needsMonth && !monthLabel(month)
    ? 'Pick a month.'
    : picked.hasValueOnly
    ? null
    : mode === 'fixed'
    ? !fixedValue.trim()
      ? 'Enter the target number.'
      : !isTargetNumber(fixedValue)
      ? 'A target must be a number, 0 or more.'
      : null
    : mode === 'own'
    ? !ownField
      ? 'Pick the detail that holds their number.'
      : null
    : !perField
    ? 'Pick the detail the numbers go by.'
    : perRows.length === 0
    ? 'Enter a target for at least one value.'
    : perRows.some((r) => !isTargetNumber(r.raw))
    ? 'A target must be a number, 0 or more.'
    : null;

  const target: LevelTargetInput | null = !picked
    ? null
    : picked.hasValueOnly
    ? { mode: 'has_value' }
    : mode === 'fixed'
    ? { mode: 'fixed', value: Number(fixedValue) }
    : mode === 'own'
    ? ownField && { mode: 'own', field: asRef(ownField) }
    : perField && {
        mode: 'per_value',
        field: asRef(perField),
        values: perRows.map((r) => ({ value: r.value, target: Number(r.raw) })),
      };
  const settings: ReachLevelInput | null =
    on && !missing && picked && target
      ? { source: picked.needsMonth ? { ...picked.source, month } : picked.source, target }
      : null;

  // Where the chosen audience stands, recounted shortly after any change. Asked only once
  // the form is complete, as the API refuses anything less; a failure only shows a line.
  const previewBody =
    settings && hasAudience && projectId != null
      ? JSON.stringify({ projectId, reachLevel: settings, ...audience })
      : null;
  // Whose tiles these are: another audience or another thing to check starts from none.
  const tilesKey = JSON.stringify({ projectId, audience, pickedKey });
  useEffect(() => {
    setPreviewError(null);
    if (!previewBody) {
      setCounted(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await bannerAPI.reachLevelPreview(JSON.parse(previewBody));
        if (!cancelled) setCounted({ body: previewBody, tiles: tilesKey, data: res.data?.data ?? null });
      } catch (err: any) {
        if (!cancelled) {
          setCounted(null);
          setPreviewError(err?.response?.data?.message || 'Could not work out the counts.');
        }
      }
    }, COUNT_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // The tiles' key follows the body, and only a new body or a retry counts again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewBody, previewAttempt]);

  // Asked, or about to be, and not yet answered for exactly this form.
  const counting = !!previewBody && counted?.body !== previewBody && !previewError;
  // The tiles keep the last count, dimmed, while a new target is on its way, and only for this
  // same audience. What a decision rests on, the button's words and the refusal, takes only the
  // count of this very form.
  const preview = counted?.tiles === tilesKey ? counted.data : null;
  const current = counted?.body === previewBody ? counted?.data ?? null : null;

  // Today's words for a score; a figure otherwise.
  const isScore = picked?.group === 'Score';
  const word = isScore ? 'score' : 'figure';
  const doneWhen = hasValue ? 'it has a value' : `their ${word} reaches the target`;

  // The personal line the server puts above the message, with this form's numbers: the value
  // with the highest target, and a figure just under it.
  const top = perRows
    .filter((r) => isTargetNumber(r.raw))
    .reduce<{ value: string; target: number } | null>(
      (best, r) => (!best || Number(r.raw) > best.target ? { value: r.value, target: Number(r.raw) } : best),
      null
    );
  const example = (() => {
    if (!on) return { line: null, target: undefined, figure: undefined };
    if (picked?.hasValueOnly) return { line: `${picked.label}: not filled in yet`, target: undefined, figure: undefined };
    const number =
      mode === 'per_value'
        ? top?.target
        : mode === 'fixed' && fixedValue.trim() && isTargetNumber(fixedValue)
        ? Number(fixedValue)
        : mode === 'own'
        ? 90
        : undefined;
    if (number === undefined) return { line: `Store … · Target … · Your ${word} …`, target: undefined, figure: undefined };
    // As the server's line: their store's name, but for a score per store category.
    const place = mode === 'per_value' && top && isScoreSource && perKey === STORE_CATEGORY ? `Store ${top.value}` : 'Demo Store';
    const figure = Math.floor(number * 0.84);
    return {
      line:
        number > 0
          ? `${place} · Target ${number} · Your ${word} ${figure}`
          : `${place} · Target ${number} · No ${word} yet`,
      target: String(number),
      figure: String(figure),
    };
  })();

  // The form is complete, but the latest count says nobody in this audience has a target:
  // the API would refuse the send, so say why here instead.
  const isCategoryScore = preview?.storedAs === 'score_target';
  const blocked =
    missing ??
    (previewError
      ? 'The count failed. Try again below.'
      : counting
      ? 'Counting…'
      : current && current.belowTarget + current.alreadyMet === 0
      ? `Nobody in this audience has a target.${isCategoryScore ? ' Add one for their store category.' : ''}`
      : null);
  // Once the count is in, the button says how many of this audience the send reaches: people
  // with no target are left out, and the "already met" get a done row.
  const reach = on && current ? { reach: current.belowTarget + current.alreadyMet, of: current.total } : null;

  return {
    sources: loaded,
    sourcesError,
    retrySources: () => setAttempt((n) => n + 1),
    picked,
    pickedKey,
    pickSource,
    sourceKey,
    month,
    pickMonth,
    mode,
    setMode,
    offersOwn,
    offersPer,
    hasValue,
    fixedValue,
    setFixedValue,
    ownKey,
    setOwnKey,
    perKey,
    pickPerField: setPerKey,
    perField,
    perFieldLabel: perField ? detailLabel(perField) : '',
    perValues,
    addPerValue,
    boxValue,
    typeBox,
    missing,
    blocked,
    settings,
    preview,
    previewLoading: counting,
    previewError,
    retryPreview: () => setPreviewAttempt((n) => n + 1),
    isCategoryScore,
    word,
    doneWhen,
    example,
    reach,
    nameOfMonth,
  };
}

export type ReachLevel = ReturnType<typeof useReachLevel>;
